import { LocalLink } from '@shared/util/link'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
    type ComponentProps,
    type FunctionComponent,
    type ReactNode,
    useEffect,
    useState,
} from 'react'

/**
 * A Next.js Link that automatically preserves attribution parameters during navigation.
 * Use this for internal navigation links in header, footer, sidebar, etc.
 *
 * Attribution params preserved: UTM params, gclid, fbclid, pid, af_*, ttclid, ScCid, etc.
 */
export const AttributionLink: FunctionComponent<AttributionLinkProps> = ({
    href,
    children,
    ...props
}) => {
    const router = useRouter()
    const [serializedHref, setSerializedHref] = useState(href)

    // Update the serialized href when router becomes ready or asPath changes
    // This ensures attribution params are preserved after hydration
    useEffect(() => {
        if (router.isReady) {
            const link = new LocalLink({ base: href })
            setSerializedHref(link.serialize(router))
        }
    }, [href, router.isReady, router.asPath])

    return (
        <Link href={serializedHref} {...props}>
            {children}
        </Link>
    )
}

interface AttributionLinkProps extends Omit<
    ComponentProps<typeof Link>,
    'href'
> {
    href: string
    children: ReactNode
}

export default AttributionLink
